import { request } from "../client"

export type BacklogListSourceSystem =
  | "portal_kamin_futura"
  | "portal_schocom"
  | "portal_flobay8"
  | "portal_uno_office"
  | "portal_dexheimer_agzess"
  | "isi_export"

export type BacklogDurationStrategy =
  | { method: "typical" }
  | { method: "price"; hourly_rate_cents: number; minimum_price_cents: number }
  | { method: "fixed"; duration_minutes: number }

type BacklogWorkChoice =
  | { mode: "automatic"; order_type_keys: string[] }
  | { mode: "manual"; order_type_ids: string[] }

export type BacklogListImportOptions =
  | ({
      document_sha256: string
      contract_version: 2
      evaluation_date?: string | null
      duration_strategy: BacklogDurationStrategy
    } & BacklogWorkChoice)
  | ({
      document_sha256: string
      contract_version: 1
      evaluation_date?: string | null
    } & (
      | { mode: "automatic"; order_type_keys: string[] }
      | { mode: "manual"; order_type_ids: string[]; duration_minutes: number }
    ))

export type BacklogListInspectionIssue = {
  code: string
  message: string
  columns: string[]
  row_number?: number | null
}

export type BacklogListInspection = {
  source_system: BacklogListSourceSystem | null
  document_sha256: string
  contract_version: 1 | 2
  evaluation_date?: string | null
  parsed_count: number
  errors: BacklogListInspectionIssue[]
  warnings: BacklogListInspectionIssue[]
  detected_work_types: Array<{ key: string; row_count: number }>
  help_topic: string
  price_supported?: boolean
  priced_row_count?: number
  unclear_price_row_count?: number
}

export const backlogListSourceNames: Record<BacklogListSourceSystem, string> = {
  portal_kamin_futura: "Kamin Futura",
  portal_uno_office: "UNO Office",
  portal_schocom: "SchoCom",
  portal_flobay8: "Fertl Florian / FloBay8",
  portal_dexheimer_agzess: "Dexheimer AGZESS",
  isi_export: "ISI-Export",
}

export const inspectPlanningBacklogList = async ({
  organizationId,
  file,
  signal,
}: {
  organizationId: string
  file: File
  signal?: AbortSignal
}): Promise<BacklogListInspection> => {
  const body = new FormData()
  body.set("organization_id", organizationId)
  body.set("file", file)

  return request<BacklogListInspection>("/planning/backlog-lists/inspect", {
    method: "POST",
    body,
    signal,
  })
}

export type BacklogListResolveRowStatus = "matched" | "unmatched" | "ambiguous"

export type BacklogListMatchWarning = {
  code: "city_mismatch" | "postal_code_mismatch"
  source_value: string
  target_value: string | null
}

export type BacklogListResolveRowFields = {
  customer_number: string
  address?: string | null
}

export type BacklogListResolveRow = {
  source_row_id: string
  page_number: number
  row_number: number
  fields: BacklogListResolveRowFields
  status: BacklogListResolveRowStatus
  property_id: string | null
  property_unit_id?: string | null
  match_reason?:
    | "number_and_address"
    | "number_address_conflict"
    | "address_unverifiable"
    | "address"
    | "address_and_location"
    | "unit_number"
    | "import_reference"
    | "ambiguous"
    | "not_found"
    | "target_unavailable"
    | null
  match_warnings?: BacklogListMatchWarning[]
  source_values?: Array<string | null>
  source_uncertain_columns?: number[]
  source_line_start?: number | null
  source_line_end?: number | null
  source_identity?: string | null
  price_components?: BacklogPriceComponent[]
  suggested_order_type_ids?: string[]
  work_evidence?: Array<{
    slot: string
    column: string
    value: string
    status: "open" | "done" | "empty" | "unknown"
    order_type_key: string | null
    reconsidered_from_date?: string | null
  }>
  work_warnings?: Array<{
    code:
      | "unknown_code"
      | "unknown_value"
      | "unmapped_work"
      | "conflicting_work"
      | "missing_work_columns"
      | "unsupported_layout"
      | "no_open_work"
    column: string
    value: string
  }>
}

export type BacklogPriceComponent = {
  source_row_id: string
  column_index: number
  slot: string
  order_type_key: string | null
  value: string
  status: "priced" | "unknown" | "done" | "empty" | "conflict" | "duplicate"
  amount_cents: number | null
}

export type BacklogListResolveSummary = {
  parsed_count: number
  unique_customer_number_count: number
  matched_count: number
  unmatched_count: number
  ambiguous_count: number
  selected_property_count: number
}

export type BacklogListResolveResponse = {
  source_system: BacklogListSourceSystem
  import_options?: BacklogListImportOptions | null
  source_columns?: string[]
  rows: BacklogListResolveRow[]
  properties: Property[]
  selected_property_ids: string[]
  summary: BacklogListResolveSummary
}

export const resolvePlanningBacklogList = async ({
  organizationId,
  sourceSystem,
  file,
  options,
  signal,
}: {
  organizationId: string
  sourceSystem?: BacklogListSourceSystem
  file: File
  options?: BacklogListImportOptions
  signal?: AbortSignal
}): Promise<BacklogListResolveResponse> => {
  const body = new FormData()
  body.set("organization_id", organizationId)

  if (sourceSystem) body.set("source_system", sourceSystem)

  if (options) body.set("options", JSON.stringify(options))

  body.set("file", file)

  return request<BacklogListResolveResponse>("/planning/backlog-lists/resolve", {
    method: "POST",
    body,
    signal,
  })
}

const errorStatus = (error: unknown): number | null => {
  if (!error || typeof error !== "object") return null

  const candidate = error as { status?: unknown; statusCode?: unknown }
  const status = candidate.status ?? candidate.statusCode

  return typeof status === "number" ? status : null
}

const errorReasonCode = (error: unknown): string | null => {
  if (!error || typeof error !== "object") return null

  const candidate = error as {
    reasonCode?: unknown
    payload?: { reason_code?: unknown; detail?: { reason_code?: unknown } }
  }
  const reasonCode =
    candidate.reasonCode ?? candidate.payload?.reason_code ?? candidate.payload?.detail?.reason_code

  return typeof reasonCode === "string" ? reasonCode : null
}

export const backlogListNeedsReinspection = (error: unknown): boolean =>
  errorReasonCode(error) === "backlog_list_evaluation_date_changed"

const backlogListLayoutErrorMessage = (sourceSystem?: BacklogListSourceSystem): string => {
  if (sourceSystem === "portal_flobay8") {
    return "Bitte verwenden Sie die vollständige Florian-Resteliste Planung als Text-PDF. Die Liegenschaftsliste gehört in den Kehrbezirksimport."
  }

  if (sourceSystem === "portal_dexheimer_agzess") {
    return "Die PDF entspricht keiner vollständig lesbaren Dexheimer-Arbeitsliste oder -Restearbeitsliste. Bitte exportieren Sie die Liste erneut aus Dexheimer AGZESS."
  }

  if (sourceSystem === "portal_kamin_futura") {
    return "Die CSV entspricht nicht der festen Kamin-Futura-Exportvorlage. Bitte prüfen Sie die Spalten über die Exporthilfe."
  }

  if (sourceSystem === "portal_schocom") {
    return "Die PDF entspricht keiner unterstützten SchoCom-Arbeitsrückstandsliste."
  }

  return "Die PDF entspricht keiner unterstützten UNO-Office-Rückstandsliste."
}

const sourceSystemForReasonCode = (
  reasonCode: string | null,
  selectedSourceSystem?: BacklogListSourceSystem,
): BacklogListSourceSystem | undefined => {
  if (reasonCode?.startsWith("florian_backlog_pdf_")) return "portal_flobay8"

  if (reasonCode?.startsWith("dexheimer_backlog_pdf_")) return "portal_dexheimer_agzess"

  if (reasonCode?.startsWith("schocom_backlog_pdf_")) return "portal_schocom"

  if (reasonCode?.startsWith("uno_office_backlog_pdf_")) return "portal_uno_office"

  if (reasonCode?.startsWith("kamin_futura_backlog_csv_")) return "portal_kamin_futura"

  if (reasonCode?.startsWith("hottgenroth_open_work_csv_")) return "portal_kamin_futura"

  return selectedSourceSystem
}

const hasDocumentReason = (reasonCode: string | null, suffix: string): boolean =>
  reasonCode === `backlog_list_pdf_${suffix}` ||
  reasonCode === `uno_office_backlog_pdf_${suffix}` ||
  reasonCode === `schocom_backlog_pdf_${suffix}` ||
  reasonCode === `florian_backlog_pdf_${suffix}` ||
  reasonCode === `dexheimer_backlog_pdf_${suffix}` ||
  reasonCode === `kamin_futura_backlog_csv_${suffix}` ||
  reasonCode === `hottgenroth_open_work_csv_${suffix}`

const missingBaselineMessage = (sourceSystem?: BacklogListSourceSystem): string =>
  sourceSystem === "portal_flobay8"
    ? "Für diese Organisation wurde noch kein abgeschlossener Florian-Import gefunden. Importieren Sie zuerst die vollständige Liegenschaftsliste über den Kehrbezirksimport."
    : sourceSystem === "portal_kamin_futura"
      ? "Für diese Organisation wurde noch kein abgeschlossener Kamin-Futura-Stammdatenimport gefunden. Importieren Sie zuerst den Kamin-Futura-Kundenstamm."
      : "Für diese Organisation wurde noch kein abgeschlossener SchoCom-Stammdatenimport gefunden. Importieren Sie zuerst den SchoCom-Kundenstamm."

const incompatibleBaselineMessage = (sourceSystem?: BacklogListSourceSystem): string =>
  sourceSystem === "portal_flobay8"
    ? "Der letzte Florian-Import enthält keine kompatiblen Einheitszuordnungen. Importieren Sie die vollständige Liegenschaftsliste erneut."
    : sourceSystem === "portal_kamin_futura"
      ? "Der letzte Kamin-Futura-Stammdatenimport enthält noch keine kompatiblen Einheitszuordnungen. Importieren Sie den Kamin-Futura-Kundenstamm erneut."
      : "Der letzte SchoCom-Stammdatenimport enthält noch keine kompatiblen Einheitszuordnungen. Importieren Sie den SchoCom-Kundenstamm erneut."

export const backlogListResolveErrorMessage = (
  error: unknown,
  sourceSystem?: BacklogListSourceSystem,
): string => {
  const status = errorStatus(error)
  const reasonCode = errorReasonCode(error)

  if (reasonCode === "backlog_list_document_changed")
    return "Die Datei wurde ausgetauscht. Bitte prüfen Sie sie erneut."

  if (backlogListNeedsReinspection(error))
    return "Der Prüfungstag hat sich geändert. Bitte prüfen Sie die Datei erneut."

  if (
    reasonCode === "backlog_list_options_invalid" ||
    reasonCode === "backlog_list_order_types_invalid"
  )
    return "Bitte prüfen Sie die Auftragstypen und die Dauer."

  if (reasonCode === "backlog_list_work_selection_invalid")
    return "Bitte wählen Sie mindestens eine in der Datei erkannte Arbeitsart."

  if (reasonCode === "kamin_futura_backlog_csv_columns_invalid")
    return backlogListLayoutErrorMessage("portal_kamin_futura")

  if (
    reasonCode === "backlog_list_source_unknown" ||
    reasonCode === "backlog_list_source_ambiguous" ||
    reasonCode === "backlog_list_source_mismatch"
  )
    return "Das Listenformat wurde nicht eindeutig erkannt. Bitte prüfen Sie die Datei über die Exporthilfe."

  if (reasonCode === "backlog_order_type_catalog_incomplete")
    return "Ein benötigter System-Auftragstyp fehlt. Bitte lassen Sie den Auftragstyp-Katalog prüfen."

  const errorSourceSystem = sourceSystemForReasonCode(reasonCode, sourceSystem)
  const isSchoCom = errorSourceSystem === "portal_schocom"
  const isKaminFutura = errorSourceSystem === "portal_kamin_futura"

  if (status === 413 || reasonCode === "backlog_list_file_too_large") {
    return isKaminFutura
      ? "Die Resteliste ist zu groß. Bitte laden Sie eine kleinere CSV-Datei hoch."
      : "Die Rückstandsliste ist zu groß. Bitte laden Sie eine kleinere PDF-Datei hoch."
  }

  if (status === 415 || reasonCode === "backlog_list_unsupported_media_type") {
    return "Bitte wählen Sie eine gültige CSV- oder PDF-Datei aus."
  }

  if (
    reasonCode === "backlog_list_source_system_unsupported" ||
    reasonCode === "backlog_list_source_unsupported"
  ) {
    return "Das ausgewählte Kehrbezirksprogramm wird nicht unterstützt."
  }

  if (reasonCode === "backlog_list_import_baseline_missing") {
    return missingBaselineMessage(errorSourceSystem)
  }

  if (reasonCode === "backlog_list_import_baseline_incompatible") {
    return incompatibleBaselineMessage(errorSourceSystem)
  }

  if (hasDocumentReason(reasonCode, "unreadable")) {
    if (isKaminFutura) return "Die Kamin-Futura-Resteliste konnte nicht gelesen werden."

    if (isSchoCom) return "Die SchoCom-Arbeitsrückstandsliste konnte nicht gelesen werden."

    return "Die Rückstandsliste konnte nicht gelesen werden."
  }

  if (hasDocumentReason(reasonCode, "encrypted")) {
    if (isSchoCom)
      return "Passwortgeschützte SchoCom-Arbeitsrückstandslisten werden nicht unterstützt."

    return "Passwortgeschützte Rückstandslisten werden nicht unterstützt."
  }

  if (hasDocumentReason(reasonCode, "empty") || hasDocumentReason(reasonCode, "text_missing")) {
    if (isKaminFutura) return "Die Kamin-Futura-Resteliste enthält keinen auslesbaren Inhalt."

    if (isSchoCom) return "Die SchoCom-Arbeitsrückstandsliste enthält keinen auslesbaren Inhalt."

    return "Die Rückstandsliste enthält keinen auslesbaren Inhalt."
  }

  if (
    hasDocumentReason(reasonCode, "page_limit_exceeded") ||
    hasDocumentReason(reasonCode, "row_limit_exceeded") ||
    hasDocumentReason(reasonCode, "content_limit_exceeded")
  ) {
    if (isKaminFutura) return "Die Kamin-Futura-Resteliste enthält zu viele Einträge."

    if (isSchoCom) return "Die SchoCom-Arbeitsrückstandsliste enthält zu viele Einträge."

    return "Die Rückstandsliste enthält zu viele Einträge."
  }

  if (hasDocumentReason(reasonCode, "rows_missing")) {
    if (isKaminFutura) return "In der Kamin-Futura-Resteliste wurden keine Einträge gefunden."

    if (isSchoCom) return "In der SchoCom-Arbeitsrückstandsliste wurden keine Gebäude gefunden."

    return "In der Rückstandsliste wurden keine Datensätze gefunden."
  }

  if (hasDocumentReason(reasonCode, "field_limit_exceeded")) {
    return isKaminFutura
      ? "Ein Eintrag der CSV-Rückstandsliste ist zu lang."
      : "Ein Eintrag der PDF-Rückstandsliste ist zu lang."
  }

  if (reasonCode === "kamin_futura_backlog_csv_customer_number_invalid") {
    return "Die Kamin-Futura-Resteliste enthält eine ungültige Liegenschaftsnummer."
  }

  if (reasonCode === "schocom_backlog_pdf_customer_number_unsupported") {
    return "Die SchoCom-Arbeitsrückstandsliste enthält eine nicht unterstützte Kundennummer."
  }

  if (
    hasDocumentReason(reasonCode, "layout_invalid") ||
    hasDocumentReason(reasonCode, "record_count_mismatch") ||
    hasDocumentReason(reasonCode, "row_count_mismatch") ||
    reasonCode === "schocom_backlog_pdf_building_count_mismatch"
  ) {
    return backlogListLayoutErrorMessage(errorSourceSystem)
  }

  if (status === 422) {
    return "Die Rückstandsliste konnte nicht eindeutig verarbeitet werden."
  }

  return "Die Rückstandsliste konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut."
}
